/**
 * Common utility functions
 */

import { curry } from "ramda"

// By default TS will infer `string[]` for an array so use this function to
// extract string literal unions.
const stringLiteralArray = <LiteralString extends string>(
  xs: LiteralString[]
) => xs

const capitalize = (str: string) =>
  !str ? str : str[0].toUpperCase() + str.slice(1)

const slugify = (label: string) => {
  return label
    .trim()
    .toLowerCase()
    .replace(/ /g, "-")
    .replace(/[^\w-]+/g, "")
}

// Inverses the nested keys
const inverseProp = curry((key, { [key]: prop }) =>
  Object.entries(prop).reduce(
    (acc, [k, v]) => ({
      ...acc,
      [k]: { [key]: v }
    }),
    {}
  )
)

const coerceArray = <T>(value: T | T[]): T[] =>
  Array.isArray(value) ? value : [value]

// Eviscerate props
const inverseProps = <P>(props: P) =>
  Object.keys(props).reduce((acc, k) => {
    const inverse = inverseProp(k, props)
    return Object.entries(inverse).reduce(
      (acc2: Record<string, any>, [k, v]) => {
        if (acc2[k] === undefined) {
          acc2[k] = {}
        }
        Object.assign(acc2[k], v)
        return acc2
      },
      acc
    )
  }, {})

// Creates a new object with the own properties of the provided object, but the
// keys renamed according to logic of renaming function
const renameKeysWith = curry(
  (fn: (s: string) => string, obj: Record<string, any>) =>
    Object.entries(obj).reduce(
      (acc, [k, v]) => ({
        ...acc,
        [fn(k)]: v
      }),
      {}
    )
)

export {
  coerceArray,
  capitalize,
  inverseProp,
  inverseProps,
  renameKeysWith,
  stringLiteralArray,
  slugify
}
